import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchActiveUsers,
  getQueryKey,
  selectTableState,
  setQuery as setQueryAction,
  setSortOrder as setSortOrderAction,
} from "../store/activeUsersSlice";

/**
 * Данные таблицы хранятся в redux по tableKey вместе с фильтрами.
 * При возврате на страницу запрос не повторяется, если фильтры не менялись —
 * берутся данные из стора. Свежая загрузка — только после перезагрузки страницы
 * (стор пустой) или при изменении фильтров/страницы.
 *
 * initialQuery используется только пока query таблицы ещё не проинициализирован
 * (первый заход после перезагрузки — берём фильтры из URL).
 */
export function useActiveUsers(tableKey, initialQuery) {
  const dispatch = useDispatch();
  const table = useSelector(selectTableState(tableKey));

  const query = table.initialized ? table.query : initialQuery;
  const queryKey = getQueryKey(query);

  useEffect(() => {
    if (!table.initialized) {
      dispatch(setQueryAction({ tableKey, query }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey, table.initialized]);

  // запрос только если в сторе нет данных для текущего набора фильтров
  useEffect(() => {
    if (table.fetchedKey === queryKey) return;
    dispatch(fetchActiveUsers({ tableKey, query }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey, queryKey, table.fetchedKey]);

  // если прошлая загрузка упала — при возврате на страницу пробуем ещё раз
  useEffect(() => {
    if (table.error && !table.loading) {
      dispatch(fetchActiveUsers({ tableKey, query }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  const setQuery = useCallback(
    (updater) => {
      const next = typeof updater === "function" ? updater(query) : updater;
      dispatch(setQueryAction({ tableKey, query: next }));
    },
    [dispatch, tableKey, query],
  );

  const setSortOrder = useCallback(
    (sortOrder) => dispatch(setSortOrderAction({ tableKey, sortOrder })),
    [dispatch, tableKey],
  );

  return {
    query,
    queryKey,
    setQuery,
    data: table.data,
    total: table.total,
    // пока запрос под новые фильтры не стартовал — тоже считаем загрузкой, чтобы не мигало "Нет данных"
    loading: table.loading || table.fetchedKey !== queryKey,
    error: table.error,
    sortOrder: table.sortOrder,
    setSortOrder,
  };
}
